import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useMinisiteSessionAnalytics } from '../analytics/webAnalytics';
import MonthlyLeadersScreen from './minisite/MonthlyLeadersScreen';
import RemoteNameKeyboard from './minisite/RemoteNameKeyboard';
import { fetchMinisiteLeaderboard, submitMinisiteLeaderboard } from '../api/minisiteLeaderboard';
import { qualifiesForLeaderboard } from '../utils/minisiteLeaderboardQualify';
import './SlotMachineGame.css';
import './minisite/MinisiteShared.css';

const SYMBOLS = [
  { id: 0, face: '🍒' },
  { id: 1, face: '🍋' },
  { id: 2, face: '⭐' },
  { id: 3, face: '💎' },
  { id: 4, face: '7️⃣' },
];

/** Vegas-style: payout = multiplier × line bet (triple of a kind) */
const TRIPLE_MULT = [10, 25, 50, 100, 500];
/** Two of a kind on payline — classic small credit */
const PAIR_MULT = 2;

const BET_OPTIONS = [1, 5, 10];
const START_TOKENS = 100;
const SCORE_CAP = 500000;
const SPIN_MS = 1550;
/** רצועת סמלים — חייבת להיות ארוכה מעבר לאינדקס המרבי בזמן סיבוב */
const STRIP_CELLS = 240;
/**
 * אינדקס תא עליון שמציג את סמל s (0..4) — 240%5=0 אז 60+s מיושר לסמלים
 */
function snapReelTopToSymbol(s) {
  return 60 + s;
}

function stripSymbols(count) {
  return Array.from({ length: count }, (_, i) => SYMBOLS[i % SYMBOLS.length]);
}

/**
 * Win in tokens for this spin (Vegas: multiples of the wager).
 * @param {number} bet
 */
function computeWinTokens(s0, s1, s2, bet) {
  if (s0 === s1 && s1 === s2) return TRIPLE_MULT[s0] * bet;
  if (s0 === s1 || s1 === s2 || s0 === s2) return PAIR_MULT * bet;
  return 0;
}

function nextStripIndex(current, desiredSymbolId) {
  const rounds = 28;
  const step = ((desiredSymbolId - (current % SYMBOLS.length)) + SYMBOLS.length) % SYMBOLS.length;
  return current + rounds * SYMBOLS.length + step;
}

/** Largest affordable bet option */
function clampBet(tokens, bet) {
  const ok = BET_OPTIONS.filter((b) => b <= tokens);
  if (ok.length === 0) return 1;
  if (ok.includes(bet)) return bet;
  return ok[ok.length - 1];
}

function SlotMachineGame() {
  useMinisiteSessionAnalytics();
  const [cellH, setCellH] = useState(140);
  const [screen, setScreen] = useState('leaders');
  const prevScreenRef = useRef('leaders');

  const [reelPos, setReelPos] = useState([62, 63, 64]);
  const [spinning, setSpinning] = useState(false);
  const [lastWin, setLastWin] = useState(0);
  /** Running balance (אסימונים) */
  const [tokens, setTokens] = useState(START_TOKENS);
  /** Cumulative tokens won — used for leaderboard (סה״כ זכיות) */
  const [totalWon, setTotalWon] = useState(0);
  const [bet, setBet] = useState(1);

  const [phase, setPhase] = useState('running');
  const [deadStep, setDeadStep] = useState('idle');
  const [joinSel, setJoinSel] = useState('no');
  const [nameInput, setNameInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const spinTimerRef = useRef(null);
  const scoreRef = useRef(0);

  useEffect(() => {
    scoreRef.current = totalWon;
  }, [totalWon]);

  useEffect(() => {
    setBet((b) => clampBet(tokens, b));
  }, [tokens]);

  const goToLeaders = useCallback(() => {
    if (spinTimerRef.current) {
      clearTimeout(spinTimerRef.current);
      spinTimerRef.current = null;
    }
    setPhase('running');
    setDeadStep('idle');
    setNameInput('');
    setJoinSel('no');
    setReelPos([62, 63, 64]);
    setSpinning(false);
    setLastWin(0);
    setTokens(START_TOKENS);
    setTotalWon(0);
    scoreRef.current = 0;
    setBet(1);
    setScreen('leaders');
  }, []);

  useEffect(() => {
    const ro = () => {
      const h = window.innerHeight;
      const reserve = Math.round(Math.min(300, Math.max(228, h * 0.36)));
      const v = Math.round(Math.max(68, Math.min(150, h - reserve)));
      setCellH(v);
    };
    ro();
    window.addEventListener('resize', ro);
    return () => window.removeEventListener('resize', ro);
  }, []);

  const startRound = useCallback(() => {
    setReelPos([62, 63, 64]);
    setSpinning(false);
    setLastWin(0);
    setTokens(START_TOKENS);
    setTotalWon(0);
    scoreRef.current = 0;
    setBet(1);
    setPhase('running');
    setDeadStep('idle');
  }, []);

  useEffect(() => {
    const was = prevScreenRef.current;
    prevScreenRef.current = screen;
    if (screen !== 'game') return undefined;
    if (was !== 'leaders') return undefined;
    startRound();
    return undefined;
  }, [screen, startRound]);

  useEffect(() => {
    if (phase !== 'dead') return undefined;
    let cancelled = false;
    setDeadStep('loading');
    (async () => {
      try {
        const d = await fetchMinisiteLeaderboard('slots');
        if (cancelled) return;
        const entries = d.entries || [];
        const sc = Math.min(Math.floor(scoreRef.current), SCORE_CAP);
        const show = qualifiesForLeaderboard('slots', entries, sc);
        setDeadStep(show ? 'joinAsk' : 'restartOnly');
      } catch {
        if (!cancelled) setDeadStep('restartOnly');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase]);

  useEffect(() => {
    if (phase === 'running') setDeadStep('idle');
  }, [phase]);

  const finishSpin = useCallback((winAmount, s0, s1, s2) => {
    setLastWin(winAmount);
    setSpinning(false);
    setReelPos([snapReelTopToSymbol(s0), snapReelTopToSymbol(s1), snapReelTopToSymbol(s2)]);
    setTokens((prev) => {
      const next = prev + winAmount;
      if (next <= 0) {
        setTimeout(() => setPhase('dead'), 60);
      }
      return next;
    });
    if (winAmount > 0) {
      setTotalWon((w) => {
        const nw = Math.min(SCORE_CAP, w + winAmount);
        scoreRef.current = nw;
        return nw;
      });
    }
  }, []);

  const runSpin = useCallback(() => {
    if (spinning || phase !== 'running') return;
    const b = clampBet(tokens, bet);
    if (tokens < b) return;

    setSpinning(true);
    setLastWin(0);

    const s0 = Math.floor(Math.random() * SYMBOLS.length);
    const s1 = Math.floor(Math.random() * SYMBOLS.length);
    const s2 = Math.floor(Math.random() * SYMBOLS.length);
    const winAmount = computeWinTokens(s0, s1, s2, b);

    setTokens((t) => t - b);

    setReelPos((prev) => {
      const p0 = snapReelTopToSymbol(prev[0] % SYMBOLS.length);
      const p1 = snapReelTopToSymbol(prev[1] % SYMBOLS.length);
      const p2 = snapReelTopToSymbol(prev[2] % SYMBOLS.length);
      return [
        nextStripIndex(p0, s0),
        nextStripIndex(p1, s1),
        nextStripIndex(p2, s2),
      ];
    });

    if (spinTimerRef.current) clearTimeout(spinTimerRef.current);
    spinTimerRef.current = setTimeout(() => {
      spinTimerRef.current = null;
      finishSpin(winAmount, s0, s1, s2);
    }, SPIN_MS);
  }, [spinning, phase, tokens, bet, finishSpin]);

  useEffect(
    () => () => {
      if (spinTimerRef.current) clearTimeout(spinTimerRef.current);
    },
    []
  );

  const cycleBet = useCallback(
    (dir) => {
      if (spinning || phase !== 'running') return;
      const affordable = BET_OPTIONS.filter((x) => x <= tokens);
      if (affordable.length === 0) return;
      const idx = affordable.indexOf(clampBet(tokens, bet));
      const i = idx < 0 ? 0 : idx;
      const next = affordable[(i + dir + affordable.length) % affordable.length];
      setBet(next);
    },
    [spinning, phase, tokens, bet]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (deadStep === 'name') return;

      if (phase === 'dead' && deadStep === 'loading') {
        e.preventDefault();
        return;
      }

      if (phase === 'dead' && deadStep === 'joinAsk') {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          setJoinSel((v) => (v === 'no' ? 'yes' : 'no'));
          e.preventDefault();
          return;
        }
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          if (joinSel === 'yes') {
            setNameInput('');
            setDeadStep('name');
            return;
          }
          goToLeaders();
        }
        return;
      }

      if (phase === 'dead' && deadStep === 'thanks') {
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          goToLeaders();
        }
        return;
      }

      if (e.key === 'Enter' || e.keyCode === 13) {
        if (phase === 'dead' && deadStep === 'restartOnly') {
          e.preventDefault();
          startRound();
          return;
        }
        if (phase === 'running' && !spinning && tokens > 0) {
          const b = clampBet(tokens, bet);
          if (tokens >= b) {
            e.preventDefault();
            runSpin();
          }
        }
        return;
      }

      if (phase === 'running' && !spinning) {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          cycleBet(e.key === 'ArrowUp' ? 1 : -1);
          e.preventDefault();
        }
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, deadStep, joinSel, goToLeaders, startRound, runSpin, spinning, tokens, bet, cycleBet]);

  const handleNameDone = useCallback(async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await submitMinisiteLeaderboard(
        'slots',
        trimmed,
        Math.floor(Math.min(scoreRef.current, SCORE_CAP))
      );
      setDeadStep('thanks');
    } catch {
      setDeadStep('thanks');
    } finally {
      setSubmitting(false);
    }
  }, [nameInput, submitting]);

  const handleNameCancel = useCallback(() => {
    setDeadStep('joinAsk');
    setNameInput('');
  }, []);

  const onLeadersContinue = useCallback(() => {
    setScreen('game');
  }, []);

  const strip = stripSymbols(STRIP_CELLS);
  const activeBet = clampBet(tokens, bet);
  const canSpin = phase === 'running' && !spinning && tokens > 0 && tokens >= activeBet;

  if (screen === 'leaders') {
    return (
      <MonthlyLeadersScreen
        game="slots"
        scoreLabel="סה״כ זכיות"
        onContinue={onLeadersContinue}
      />
    );
  }

  return (
    <div className="slots-page" dir="rtl">
      <div className="slots-vegas-bg" aria-hidden />
      <div className="slots-marquee" aria-hidden>
        <span className="slots-marquee-dot" />
        <span className="slots-marquee-dot" />
        <span className="slots-marquee-dot" />
        <span className="slots-marquee-dot" />
        <span className="slots-marquee-dot" />
        <span className="slots-marquee-dot" />
        <span className="slots-marquee-dot" />
        <span className="slots-marquee-dot" />
      </div>

      <div className="slots-header">
        <div className="slots-brand">
          <span className="slots-brand-lv">LAS VEGAS</span>
          <span className="slots-brand-sub">מכונת מזל</span>
        </div>
        <div className="slots-hud">
          <div className="slots-hud-item slots-hud-item--gold">
            <span className="slots-hud-label">אסימונים</span>
            <span className="slots-hud-value">{tokens}</span>
          </div>
          <div className="slots-hud-item">
            <span className="slots-hud-label">סה״כ זכיות</span>
            <span className="slots-hud-value">{totalWon}</span>
          </div>
        </div>
      </div>

      <div className="slots-cabinet">
        <div className="slots-cabinet-chrome">
          <div className="slots-machine">
            <div className="slots-reels-wrap">
              <div className="slots-reels">
                {[0, 1, 2].map((ri) => (
                  <div key={ri} className="slots-reel" style={{ '--slot-cell': `${cellH}px` }}>
                    <div
                      className={`slots-strip ${spinning ? 'slots-strip--spinning' : ''}`}
                      style={{
                        transform: `translateY(-${reelPos[ri] * cellH}px)`,
                      }}
                    >
                      {strip.map((sym, i) => (
                        <div key={`${ri}-${i}`} className="slots-cell">
                          {sym.face}
                        </div>
                      ))}
                    </div>
                    <div className="slots-window-shine" aria-hidden />
                  </div>
                ))}
              </div>
            </div>

            <div className="slots-bet-panel">
              <span className="slots-bet-title">הימור לסיבוב</span>
              <div className="slots-bet-buttons">
                {BET_OPTIONS.map((b) => {
                  const on = activeBet === b;
                  const disabled = b > tokens || spinning;
                  return (
                    <button
                      key={b}
                      type="button"
                      className={`slots-bet-btn ${on ? 'slots-bet-btn--on' : ''} ${disabled ? 'slots-bet-btn--disabled' : ''}`}
                      disabled={disabled}
                      onClick={() => !disabled && setBet(b)}
                    >
                      {b}
                    </button>
                  );
                })}
              </div>
              <p className="slots-bet-hint">↑ להימור גבוה · ↓ להימור נמוך · Enter לסיבוב</p>
            </div>

            {lastWin > 0 && !spinning && phase === 'running' && (
              <div className="slots-win-banner">+{lastWin} אסימונים</div>
            )}
          </div>
        </div>
      </div>

      {phase === 'dead' && (
        <div className="slots-overlay">
          <div className="slots-overlay-title">אין יותר אסימונים</div>
          <div className="slots-overlay-score">
            סה״כ זכיות: {Math.min(Math.floor(totalWon), SCORE_CAP)} אסימונים
          </div>
          {deadStep === 'loading' && (
            <div className="minisite-overlay-busy">בודקים זכאות לטבלה…</div>
          )}
          {deadStep === 'restartOnly' && (
            <div className="slots-overlay-hint">לחץ Enter להתחלה מחדש (100 אסימונים)</div>
          )}
          {deadStep === 'joinAsk' && (
            <div className="minisite-join">
              <p className="minisite-join-q">להצטרף למצטייני החודש?</p>
              <div className="minisite-join-options">
                <span className={`minisite-join-opt ${joinSel === 'no' ? 'minisite-join-opt--on' : ''}`}>
                  לא
                </span>
                <span className={`minisite-join-opt ${joinSel === 'yes' ? 'minisite-join-opt--on' : ''}`}>
                  כן
                </span>
              </div>
              <div className="slots-overlay-hint">← → לבחירה · Enter לאישור</div>
            </div>
          )}
          {deadStep === 'name' && (
            <RemoteNameKeyboard
              value={nameInput}
              onChange={setNameInput}
              onDone={handleNameDone}
              onCancel={handleNameCancel}
            />
          )}
          {deadStep === 'thanks' && (
            <div className="slots-overlay-hint">נרשמת בהצלחה! לחץ Enter לחזרה למצטייני החודש</div>
          )}
          {submitting && <div className="minisite-overlay-busy">שומר…</div>}
        </div>
      )}

      <div className="slots-footer">
        <div className="slots-paytable">
          <span className="slots-paytable-title">PAYTABLE · מכפילים מההימור</span>
          <div className="slots-paytable-grid">
            {SYMBOLS.map((s, i) => (
              <span key={s.id}>
                {s.face} ×3 = {TRIPLE_MULT[i]}×
              </span>
            ))}
            <span>זוג כלשהו = {PAIR_MULT}×</span>
          </div>
        </div>
        <div className="slots-hint">
          {canSpin ? 'מוכן לסיבוב' : tokens <= 0 ? '' : 'אין מספיק אסימונים להימור'}
          {' · '}ללא קול
        </div>
      </div>
    </div>
  );
}

export default SlotMachineGame;
